import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Language } from '../config/translations';

const manualContent = {
  en: {
    welcome: {
      title: 'Welcome to Ferry Safe',
      description: 'This app helps you create safety reports during your work.',
    },
    createReport: {
      title: 'Create a Report',
      steps: [
        'Click the orange "New Report" button on the home screen',
        'Describe the safety issue as clearly as possible',
        'Add a photo of the situation if possible',
        'Press "Submit Report" to submit the report',
      ],
      photoInstructions: [
        'You can take a photo directly with the camera',
        'Or choose an existing photo from your gallery',
      ],
    },
    safetyTypes: {
      title: 'Safety Report Types',
      categories: {
        electrical_safety: {
          title: 'Electrical Safety',
          examples: [
            'Loose power cables',
            'Damaged power cables',
            'Broken power outlet',
          ],
        },
        hazardous_materials: {
          title: 'Hazardous Materials',
          examples: [
            'Leakage of hazardous materials',
            'Missing labels on packaging',
          ],
        },
        hygiene_health: {
          title: 'Hygiene/Health',
          examples: [
            'Working in dusty area',
            'Poor cleaning',
            'Dirty workplace',
          ],
        },
        environmental: {
          title: 'Environmental',
          examples: [
            'Discharge of hazardous materials into the environment',
            'Leakages',
          ],
        },
        inspections: {
          title: 'Inspections',
          examples: [
            'Work equipment not inspected',
            'Fire extinguishers not inspected',
          ],
        },
        work_equipment: {
          title: 'Work Equipment',
          examples: [
            'Work equipment in poor condition',
            'Work equipment not used properly',
          ],
        },
        emergency_response: {
          title: 'Emergency Response',
          examples: [
            'First aid kit not restocked',
            'Emergency exit blocked',
          ],
        },
        working_at_height: {
          title: 'Working at Height',
          examples: [
            'Not using proper safety equipment',
            'Safety harness in poor condition',
            'No barriers where fall hazard exists',
          ],
        },
        confined_spaces: {
          title: 'Working in Confined Spaces',
          examples: [
            'Space not measured',
            'Not following proper procedure',
          ],
        },
        psychosocial: {
          title: 'Psychosocial Work Stress',
          examples: [
            'Excessive workload',
            'Discrimination',
            'Harassment of employees',
          ],
        },
        trip_hazard: {
          title: 'Trip Hazard',
          examples: [
            'Excessive clutter on work floor',
            'Slippery floor',
          ],
        },
        ppe: {
          title: 'PPE',
          examples: [
            'Required PPE not available',
            'PPE in poor condition',
            'Not using proper PPE',
          ],
        },
        bhv_safety_round: {
          title: 'BHV/Safety Round',
          examples: [
            'BHV report during round',
            'Safety round observation',
            'BHV checklist item',
          ],
        },
        other: {
          title: 'Other',
          examples: [
            'When in doubt if the observation fits in the above categories',
          ],
        },
      },
    },
    tips: {
      title: 'Tips for a Good Report',
      items: [
        'Be as specific as possible in your description',
        'Mention the exact location of the problem',
        'Indicate if it is an urgent safety issue',
        'Add a clear photo if possible',
      ],
    },
    help: {
      title: 'Need Help?',
      description: 'Contact your supervisor if you have questions about using the app or about a specific safety situation.',
    },
  },
  nl: {
    welcome: {
      title: 'Welkom bij Ferry Safe',
      description: 'Deze app helpt u om veiligheidsmeldingen te maken tijdens uw werk.',
    },
    createReport: {
      title: 'Een melding maken',
      steps: [
        'Klik op de oranje "Nieuwe Melding" knop op het hoofdscherm',
        'Beschrijf het veiligheidsprobleem zo duidelijk mogelijk',
        'Voeg indien mogelijk een foto toe van de situatie',
        'Druk op "Verstuur Melding" om de melding in te dienen',
      ],
      photoInstructions: [
        'U kunt direct een foto maken met de camera',
        'Of een bestaande foto kiezen uit uw galerij',
      ],
    },
    safetyTypes: {
      title: 'Types veiligheidsmeldingen',
      categories: {
        electrical_safety: {
          title: 'Elektrische veiligheid',
          examples: [
            'Loshangende stroomkabels',
            'Beschadigde stroomkabels',
            'Kapot stopcontact',
          ],
        },
        hazardous_materials: {
          title: 'Gevaarlijke stoffen',
          examples: [
            'Lekken van gevaarlijke stoffen',
            'Geen etiketten op de verpakking',
          ],
        },
        hygiene_health: {
          title: 'Hygiëne/gezondheid',
          examples: [
            'Werken in stoffige ruimte',
            'Slechte schoonmaak',
            'Vieze werkplek',
          ],
        },
        environmental: {
          title: 'Milieu',
          examples: [
            'Lozing van gevaarlijke stoffen in het milieu',
            'Lekkages',
          ],
        },
        inspections: {
          title: 'Keuringen',
          examples: [
            'Arbeidsmiddelen niet gekeurd',
            'Brandblussers niet gekeurd',
          ],
        },
        work_equipment: {
          title: 'Arbeidsmiddelen',
          examples: [
            'Arbeidsmiddelen in slechte staat',
            'Arbeidsmiddelen niet op de juiste manier gebruikt',
          ],
        },
        emergency_response: {
          title: 'Bedrijfsnoodgevallen',
          examples: [
            'EHBO doos niet aangevuld',
            'Vluchtroute geblokkeerd',
          ],
        },
        working_at_height: {
          title: 'Werken op hoogte',
          examples: [
            'Geen gebruik van de juiste veiligheidsmiddelen',
            'Valharnas in slechte staat',
            'Geen afzetting waar valgevaar aanwezig is',
          ],
        },
        confined_spaces: {
          title: 'Werken in besloten ruimte',
          examples: [
            'Ruimte is niet gemeten',
            'Niet volgen van de juiste procedure',
          ],
        },
        psychosocial: {
          title: 'Psychische Sociale Arbeidsbelasting',
          examples: [
            'Te grote werkdruk',
            'Discriminatie',
            'Treiteren van medewerkers',
          ],
        },
        trip_hazard: {
          title: 'Struikelgevaar',
          examples: [
            'Veel rommel aanwezig op de werkvloer',
            'Gladde vloer',
          ],
        },
        ppe: {
          title: 'PBM\'s',
          examples: [
            'Niet aanwezig van de juiste PBM\'s',
            'PBM\'s in slechte staat',
            'Geen gebruik van de juiste PBM\'s',
          ],
        },
        bhv_safety_round: {
          title: 'BHV/Veiligheidsronde',
          examples: [
            'BHV-melding tijdens ronde',
            'Veiligheidsronde observatie',
            'BHV-checklist item',
          ],
        },
        other: {
          title: 'Anders',
          examples: [
            'Bij twijfel of de observatie past in de bovenstaande categorieën',
          ],
        },
      },
    },
    tips: {
      title: 'Tips voor een goede melding',
      items: [
        'Wees zo specifiek mogelijk in uw beschrijving',
        'Vermeld de exacte locatie van het probleem',
        'Geef aan of het een urgent veiligheidsprobleem betreft',
        'Voeg indien mogelijk een duidelijke foto toe',
      ],
    },
    help: {
      title: 'Hulp nodig?',
      description: 'Neem contact op met uw leidinggevende als u vragen heeft over het gebruik van de app of over een specifieke veiligheidssituatie.',
    },
  },
};

export const AppManualScreen = () => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    loadLanguage();
  }, []);

  const loadLanguage = async () => {
    try {
      const savedLanguage = await AsyncStorage.getItem('selectedLanguage');
      if (savedLanguage) {
        setLanguage(savedLanguage as Language);
      }
    } catch (error) {
      console.error('Error loading language:', error);
    }
  };

  const content = manualContent[language];

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.section}>
          <Text style={styles.title}>{content.welcome.title}</Text>
          <Text style={styles.description}>{content.welcome.description}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>{content.createReport.title}</Text>
          {content.createReport.steps.map((step, index) => (
            <View key={index} style={styles.instructionContainer}>
              <View style={styles.instructionIcon}>
                <Ionicons 
                  name={
                    index === 0 ? "warning-outline" :
                    index === 1 ? "create-outline" :
                    index === 2 ? "camera-outline" :
                    "send-outline"
                  } 
                  size={24} 
                  color={index === 0 ? "#FF9800" : "#005575"} 
                />
              </View>
              <View style={styles.instructionText}>
                <Text style={styles.instruction}>
                  {`${index + 1}. ${step}`}
                </Text>
                {index === 2 && content.createReport.photoInstructions.map((instruction, i) => (
                  <Text key={i} style={styles.subInstruction}>
                    - {instruction}
                  </Text>
                ))}
              </View>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>{content.safetyTypes.title}</Text>
          {Object.entries(content.safetyTypes.categories).map(([key, category]) => (
            <View key={key} style={styles.categoryContainer}>
              <Text style={styles.categoryTitle}>{category.title}</Text>
              {category.examples.map((example, index) => (
                <Text key={index} style={styles.categoryExample}>• {example}</Text>
              ))}
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>{content.tips.title}</Text>
          <View style={styles.tipContainer}>
            {content.tips.items.map((tip, index) => (
              <Text key={index} style={styles.tip}>• {tip}</Text>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>{content.help.title}</Text>
          <Text style={styles.description}>{content.help.description}</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#005575',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#005575',
    marginBottom: 16,
  },
  description: {
    fontSize: 16,
    color: '#333333',
    lineHeight: 24,
  },
  instructionContainer: {
    flexDirection: 'row',
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  instructionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F5F7FA',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  instructionText: {
    flex: 1,
  },
  instruction: {
    fontSize: 16,
    color: '#333333',
    marginBottom: 4,
  },
  subInstruction: {
    fontSize: 14,
    color: '#666666',
    marginLeft: 8,
    marginTop: 4,
  },
  tipContainer: {
    backgroundColor: '#F5F7FA',
    padding: 16,
    borderRadius: 8,
  },
  tip: {
    fontSize: 16,
    color: '#333333',
    marginBottom: 8,
    lineHeight: 24,
  },
  categoryContainer: {
    marginBottom: 20,
    backgroundColor: '#F5F7FA',
    padding: 16,
    borderRadius: 8,
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#005575',
    marginBottom: 8,
  },
  categoryExample: {
    fontSize: 16,
    color: '#333333',
    marginBottom: 4,
    paddingLeft: 8,
  },
}); 